import { useState, useRef, useEffect } from 'react';
import { sendChatMessage, extractProfile, getProviders, ChatMessage, AIProvider } from '../services/api';

interface ChatProps {
  onProfileComplete: (profile: any, messages: ChatMessage[]) => void;
}

export default function Chat({ onProfileComplete }: ChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [provider, setProvider] = useState<AIProvider>('openai');
  const [availableProviders, setAvailableProviders] = useState<AIProvider[]>([]);
  const [error, setError] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load available providers
  useEffect(() => {
    getProviders().then(data => {
      setAvailableProviders(data.providers);
      if (data.default) setProvider(data.default);
    }).catch(() => { /* ignore */ });
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Start conversation automatically
  useEffect(() => {
    if (messages.length === 0) {
      startConversation();
    }
  }, []);

  async function startConversation() {
    setIsLoading(true);
    setError(null);
    try {
      // Send an empty "start" to get the first greeting from the AI
      const result = await sendChatMessage(
        [{ role: 'user', content: 'Hallo, ich suche eine Immobilie in der Schweiz.' }],
        provider
      );
      setMessages([
        { role: 'user', content: 'Hallo, ich suche eine Immobilie in der Schweiz.' },
        { role: 'assistant', content: result.message },
      ]);
    } catch (err: any) {
      setError(err.message || 'Verbindungsfehler');
    }
    setIsLoading(false);
    inputRef.current?.focus();
  }

  async function handleSend() {
    if (!input.trim() || isLoading || isComplete) return;

    const userMessage: ChatMessage = { role: 'user', content: input.trim() };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      const result = await sendChatMessage(updatedMessages, provider);
      const assistantMessage: ChatMessage = { role: 'assistant', content: result.message };
      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);

      if (result.isComplete) {
        setIsComplete(true);
        // Auto-extract profile
        handleExtractProfile(finalMessages);
      }
    } catch (err: any) {
      setError(err.message || 'AI-Fehler');
    }
    setIsLoading(false);
    inputRef.current?.focus();
  }

  async function handleExtractProfile(chatMessages: ChatMessage[]) {
    setIsExtracting(true);
    try {
      const result = await extractProfile(chatMessages, provider);
      onProfileComplete(result.profile, chatMessages);
    } catch (err: any) {
      setError('Profilerfassung fehlgeschlagen: ' + (err.message || ''));
      setIsComplete(false);
    }
    setIsExtracting(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className="chat-container">
      {/* Provider selector */}
      {availableProviders.length > 1 && (
        <div className="chat-provider-selector">
          <label>AI Modell:</label>
          {availableProviders.map(p => (
            <button
              key={p}
              className={`provider-btn ${p === provider ? 'provider-btn--active' : ''}`}
              onClick={() => setProvider(p)}
              disabled={isLoading}
            >
              {p === 'openai' ? 'OpenAI' : 'Grok'}
            </button>
          ))}
        </div>
      )}

      {/* Messages */}
      <div className="chat-messages">
        {messages.map((msg, i) => (
          <div key={i} className={`chat-bubble chat-bubble--${msg.role}`}>
            {msg.role === 'assistant' && <div className="chat-avatar">S</div>}
            <div className="chat-content">
              {msg.content}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="chat-bubble chat-bubble--assistant">
            <div className="chat-avatar">S</div>
            <div className="chat-content chat-typing">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
          </div>
        )}
        {isExtracting && (
          <div className="chat-status">
            Profil wird erstellt...
          </div>
        )}
        {error && (
          <div className="chat-error">
            {error}
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      {!isComplete && (
        <div className="chat-input-area">
          <input
            ref={inputRef}
            type="text"
            className="chat-input"
            placeholder="Ihre Antwort eingeben..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
          />
          <button
            className="chat-send-btn"
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
          >
            Senden
          </button>
        </div>
      )}
    </div>
  );
}
